/* ============================================================
   Brand Journey — docs/prototype/botarhythm-brand-journey-v2.html
   の <script> 移植。アルゴリズム（準周期合成波・線の変形・1ハゼ段差・
   シーン色遷移）はプロトタイプと同一。変更点は名前空間化と
   Shopify テーマエディタ対応のライフサイクル管理のみ。

   ・全コードを IIFE + class BrandJourney に閉じ、グローバル汚染ゼロ
   ・shopify:section:load で再初期化 / shopify:section:unload で
     rAF・IntersectionObserver・ScrollTrigger・Lenis を完全破棄
   ・prefers-reduced-motion はプロトタイプ通り（静的1フレーム描画）
   ・motion_speed（schema 0.5–1.5）は合成波の時間係数
============================================================ */
(() => {
  'use strict';

  const PHI = 1.6180339887, R2 = Math.SQRT2;
  const SELECTOR = '[data-brand-journey]';

  /* ---------- ジャーニー4形状（尾根/うねり/焙煎曲線/湯気） ----------
     形状定数は不変なのでモジュールで一度だけ生成 */
  const N = 96, W = 680;
  const ss = (t) => t * t * (3 - 2 * t);
  /* 尾根：Patagonia ロゴ（Cerro Fitz Roy の稜線）を忠実に再現。
     左の foothill から立ち上がり、主峰 Fitz Roy（最高・鋭い尖峰）を頂点に、
     右へ Poincenot / Saint-Exupéry など段々に低くなる鋭峰が続き、
     さらに右に Cerro Torre の独立した尖峰、最後は低く収束する。
     稜線を (t, 高さ) のキーポイント列で定義し線形補間でサンプリング。
     キーポイントで鋭い頂点と谷ができ、描画時のベジェ平滑化で稜線として整う。
     高さは SVG 基準線 y=300 からの隆起量（大きいほど高い峰）。 */
  const RIDGE_KEYS = [
    [0.00, 8], [0.06, 46], [0.105, 98], [0.155, 52],     // 左：中位の峰1
    [0.205, 112], [0.255, 58],                            // 左：中位の峰2（左群で最も高い）
    [0.31, 90], [0.40, 38], [0.47, 60], [0.52, 42],       // 左：中位の峰3 → 深い谷（主峰への助走）
    [0.605, 190], [0.66, 66],                             // Fitz Roy 主峰（右寄り・突出・鋭い）→ 急降下
    [0.70, 96], [0.755, 50],                              // 右肩の副峰（主峰より低い）
    [0.82, 38], [0.90, 22], [1.00, 8],                    // 右下：低標高の裾野へ descending
  ];
  const ridgeH = (t) => {
    for (let p = 0; p < RIDGE_KEYS.length - 1; p++) {
      const a = RIDGE_KEYS[p], b = RIDGE_KEYS[p + 1];
      if (t >= a[0] && t <= b[0]) {
        return a[1] + (b[1] - a[1]) * ((t - a[0]) / (b[0] - a[0]));
      }
    }
    return RIDGE_KEYS[RIDGE_KEYS.length - 1][1];
  };
  const DROP_PTS = [[200,168],[200.7,168.9],[201.4,169.9],[202.1,170.8],[202.9,171.8],[203.6,172.7],[204.3,173.7],[205,174.6],[205.7,175.5],[206.5,176.5],[207.2,177.4],[207.9,178.4],[208.6,179.3],[209.3,180.3],[209.9,181.3],[210.6,182.2],[211.3,183.2],[211.9,184.2],[212.5,185.3],[213.1,186.3],[213.6,187.4],[214.1,188.4],[214.5,189.6],[214.8,190.7],[215,191.9],[215,193.1],[214.9,194.2],[214.8,195.4],[214.7,196.6],[214.5,197.8],[214.2,198.9],[213.8,200],[213.4,201.2],[213,202.2],[212.5,203.3],[211.9,204.3],[211.3,205.3],[210.6,206.3],[209.8,207.2],[209,208.1],[208.1,208.9],[207.2,209.6],[206.2,210.2],[205.1,210.8],[204.1,211.3],[202.9,211.6],[201.8,211.9],[200.6,212],[199.4,212],[198.2,211.9],[197.1,211.6],[195.9,211.3],[194.9,210.8],[193.8,210.2],[192.8,209.6],[191.9,208.9],[191,208.1],[190.2,207.2],[189.4,206.3],[188.7,205.3],[188.1,204.3],[187.5,203.3],[187,202.2],[186.6,201.2],[186.2,200],[185.8,198.9],[185.5,197.8],[185.3,196.6],[185.2,195.4],[185.1,194.2],[185,193.1],[185,191.9],[185.2,190.7],[185.5,189.6],[185.9,188.4],[186.4,187.4],[186.9,186.3],[187.5,185.3],[188.1,184.2],[188.7,183.2],[189.4,182.2],[190.1,181.3],[190.7,180.3],[191.4,179.3],[192.1,178.4],[192.8,177.4],[193.5,176.5],[194.3,175.5],[195,174.6],[195.7,173.7],[196.4,172.7],[197.1,171.8],[197.9,170.8],[198.6,169.9],[199.3,168.9],[200,168]];
  const SHAPES = [[], [], [], []];
  for (let i = 0; i < N; i++) {
    const t = i / (N - 1), x = t * W;
    /* 0 尾根：Fitz Roy 稜線（キーポイント線形補間） */
    SHAPES[0].push([x, 300 - ridgeH(t)]);
    /* 1 うねり：なめらかな波 */
    SHAPES[1].push([x, 268 - 34 * Math.sin(t * 15.8) - 12 * Math.sin(t * 7 + 2)]);
    /* 2 焙煎曲線：投入直後にドロップ（ターニングポイント）→ 中盤から上昇する
         典型的な焙煎プロファイル。SVGはy下向きなので「下がって上がる」=yが増えて減る。
         t≈0.78 の小さな段差は「1ハゼ」。知る人にだけ届く。 */
    const tp = 0.16;                         // ターニングポイント（投入後の最低温）
    let ry;
    if (t < tp) {
      ry = 220 + (300 - 220) * ss(t / tp);                       // チャージ→降下
    } else {
      const u = (t - tp) / (1 - tp);
      ry = 300 + (72 - 300) * (1 - Math.pow(1 - u, 1.8));        // 上昇（イーズアウト：はじめ急、のち緩やか）
    }
    ry += 10 * Math.exp(-Math.pow((t - .78) * 16, 2));           // 1ハゼの段差
    SHAPES[2].push([x, ry]);
    /* 3 カップ：琥珀色の液体から立ちのぼる、滑らかな一筆のS字。
       sin(2π) で右→左に一度だけ振れる S 字。上端ほど細く（振幅を絞る）。
       上下の長さ（y 344→90）と振幅を調整。呼吸ゆらぎは dFor 側でこのシーンのみ減衰。 */
    SHAPES[3].push(DROP_PTS[i]);
  }

  /* ---------- Lenis 共有シングルトン ----------
     Lenis はルートスクロールを司るためページに1つ。
     既存テーマ（Dawn系 Rise）との衝突確認の結論:
     ・cart_type は notification（カートドロワー無し）のため
       ルートスクロールとの干渉なし → Lenis を採用
     ・モバイルメニュー / 予測検索 / モーダル等のオーバーレイ内
       スクロールは prevent で Lenis の対象外にして保護
     ・テーマエディタ（Shopify.designMode）ではエディタ自身の
       スクロール制御と干渉するため Lenis を起動しない
     ・Lenis はネイティブのアンカースクロールを乗っ取らない設定のまま
       （anchors オプション未使用）なので既存アンカー遷移は素通し */
  let lenis = null, lenisTicker = null, lenisUsers = 0, lenisRO = null;

  function acquireLenis() {
    lenisUsers++;
    if (lenis || !window.Lenis || !window.gsap) return;
    if (window.Shopify && window.Shopify.designMode) return;
    lenis = new window.Lenis({
      lerp: .09,
      wheelMultiplier: .95,
      prevent: (node) => !!(node && node.closest &&
        node.closest('#menu-drawer, .menu-drawer, modal-dialog, predictive-search, cart-notification, cart-drawer, [data-lenis-prevent]')),
    });
    lenis.on('scroll', window.ScrollTrigger.update);
    lenisTicker = (t) => lenis.raf(t * 1000);
    window.gsap.ticker.add(lenisTicker);
    window.gsap.ticker.lagSmoothing(0);

    /* 遅延読み込み（動画ポスター・画像・アプリ埋め込み・フッター等）でページ高さが
       後から伸びると、Lenis のスクロール上限が初期値のまま頭打ちになり、
       ホイールで最下部（フッター）まで進めなくなる。高さの変化に追従して再計測する。
       ※ ScrollTrigger.refresh() は pin 再計算ループを避けるため監視内では呼ばない。 */
    const resyncLenis = () => { if (lenis && typeof lenis.resize === 'function') lenis.resize(); };
    addEventListener('load', () => { resyncLenis(); window.ScrollTrigger.refresh(); });
    [300, 900, 2000, 4000].forEach((d) => setTimeout(resyncLenis, d));
    if (window.ResizeObserver) {
      let t = 0;
      lenisRO = new ResizeObserver(() => { clearTimeout(t); t = setTimeout(resyncLenis, 150); });
      lenisRO.observe(document.body);
    }
  }

  function releaseLenis() {
    lenisUsers = Math.max(0, lenisUsers - 1);
    if (lenisUsers === 0 && lenis) {
      window.gsap.ticker.remove(lenisTicker);
      if (lenisRO) { lenisRO.disconnect(); lenisRO = null; }
      lenis.destroy();
      lenis = null;
      lenisTicker = null;
    }
  }

  /* ---------- アニメ可否の判定（非対応ブラウザは静的固定スクロールへ自動フォールバック） ----------
     必須API: Canvas 2D / requestAnimationFrame / IntersectionObserver。
     どれか欠けたら静的描画に落とし、例外でヒーローが真っ白になるのを防ぐ。 */
  function supportsMotion() {
    if (typeof requestAnimationFrame !== 'function') return false;
    if (typeof IntersectionObserver !== 'function') return false;
    try {
      const probe = document.createElement('canvas');
      if (!probe.getContext || !probe.getContext('2d')) return false;
    } catch (e) { return false; }
    return true;
  }

  class BrandJourney {
    constructor(root) {
      this.root = root;
      this.reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      /* ブラウザが必須APIを欠く場合も reduce と同様に静的描画にする（自動フォールバック） */
      this.canAnimate = supportsMotion();
      this.staticMode = this.reduce || !this.canAnimate;
      this.speed = parseFloat(root.dataset.motionSpeed) || 1;
      /* 破棄処理の登録簿。destroy() が逆順で全て実行する */
      this.disposers = [];
      this.usesLenis = false;

      this.initSeason();
      this.initHero();
      const journeyEl = root.querySelector('[data-bj-journey]');
      if (journeyEl) this.initJourney(journeyEl);
      this.initSkip();
    }

    onDispose(fn) { this.disposers.push(fn); }

    destroy() {
      for (let i = this.disposers.length - 1; i >= 0; i--) {
        try { this.disposers[i](); } catch (e) { /* 破棄は続行 */ }
      }
      this.disposers = [];
      if (this.usesLenis) { releaseLenis(); this.usesLenis = false; }
    }

    /* ---------- 里山の変遷：月に応じた、ごく薄い季節の色 ---------- */
    initSeason() {
      if (this.root.dataset.seasonalTint !== 'true') return;
      const hero = this.root.querySelector('.bj-hero');
      if (!hero) return;
      const m = new Date().getMonth() + 1;
      const season =
        (m >= 3 && m <= 5) ? '#0D3530' :   // 春：若い緑のけはい
        (m >= 6 && m <= 8) ? '#0E3B45' :   // 夏：海の青が深い
        (m >= 9 && m <= 11) ? '#23231A' :  // 秋：枯れ色のけはい
                              '#0A2A33';   // 冬：澄んだ寒色
      hero.style.setProperty('--bj-season', season);
    }

    /* ---------- ヒーロー：準周期のリズム線（二度と同じ形にならない） ---------- */
    initHero() {
      const c = this.root.querySelector('[data-bj-canvas]');
      if (!c) return;
      const ctx = c.getContext && c.getContext('2d');
      if (!ctx) return;   // Canvas2D 非対応：波は描かず背景CSSのまま（静的固定スクロール）
      const speed = this.speed;

      const size = () => {
        const dpr = Math.min(devicePixelRatio || 1, 2);
        c.width = c.clientWidth * dpr;
        c.height = c.clientHeight * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };
      size();
      addEventListener('resize', size);
      this.onDispose(() => removeEventListener('resize', size));

      /* 無理数比（PHI, √2）で重ねた波 ＋ 振幅の呼吸。
         周期が互いに割り切れないため、波形は永遠に再現されない。
         最後の項は手描きの微細な不均一（時間に依らない揺らぎ）。 */
      function lineY(x, t, w) {
        /* Rhythmwaves — 黄金比の等比則で重ねる準周期の波。
           k_c = K0·φ^c（波数は φ で上昇）／ a_c = A0·φ^-c（振幅は 1/φ で減衰）
           ⇒ 各層の最大傾き a·k は一定・総振幅は A0·φ² に収束。
           位相は黄金角 2π·frac(c·φ) で層を脱相関。時間は全層一定 ω（分散ドリフト）。
           breathe（無理数比の遅い包絡）で時間方向も再現させない＝二度と同じ形にならない。
           レスポンシブ：幅比で振幅を絞り＋高層を間引き、モバイルでピークが尖るのを抑える。 */
        const u = x / w;
        const K0 = 4.216, A0 = 15, W0 = 680, OMEGA = .21;
        const ampScale = Math.max(.72, Math.min(1.1, Math.pow(w / W0, .6)));
        const maxLayer = w < 420 ? 4 : 5;
        const breathe = 1 + .22 * Math.sin(t * .07) + .08 * Math.sin(t * .013 * PHI);
        let y = 0;
        for (let cc = 0; cc <= maxLayer; cc++) {
          const k = K0 * Math.pow(PHI, cc);
          const a = A0 * Math.pow(PHI, -cc) * ampScale;
          const ph = 2 * Math.PI * (cc * PHI - Math.floor(cc * PHI));
          y += a * Math.sin(u * k + ph - OMEGA * t);
        }
        return y * breathe;
      }

      /* 時刻の移ろい：Midnight → 夜明け → 昼間(コバルト) → 夕焼け をゆっくり一巡。
         各時刻は空(上)・水平線(中・最も明るい)・海(下)の3色。光は足さず色味のみ変える。 */
      const DAYCYCLE = [
        { sky:[9,28,34],   hz:[14,46,53],  sea:[5,16,21] },   /* Midnight（藍碧） */
        { sky:[20,22,44],  hz:[56,44,62],  sea:[9,11,24] },   /* 夜明け（藍紫→淡い暁） */
        { sky:[10,26,66],  hz:[28,56,124], sea:[6,14,40] },   /* 昼間（コバルトブルー） */
        { sky:[38,26,52],  hz:[122,60,40], sea:[14,9,18] },   /* 夕焼け（残照） */
      ];
      const DAY_PERIOD = 60;                                  /* 秒で一巡（ゆっくり） */
      const _lerp = (a, b, u) => a + (b - a) * u;
      const _mix  = (A, B, u) => [_lerp(A[0],B[0],u), _lerp(A[1],B[1],u), _lerp(A[2],B[2],u)];
      const _rgb  = c => `rgb(${c[0]|0},${c[1]|0},${c[2]|0})`;
      function dayColors(now) {
        const ph = ((now / 1000) / DAY_PERIOD * 4) % 4;
        const i = Math.floor(ph); let u = ph - i; u = u * u * (3 - 2 * u);   /* smoothstep */
        const A = DAYCYCLE[i], B = DAYCYCLE[(i + 1) % 4];
        return { sky:_rgb(_mix(A.sky,B.sky,u)), hz:_rgb(_mix(A.hz,B.hz,u)), sea:_rgb(_mix(A.sea,B.sea,u)) };
      }

      let running = false, rafId = 0;
      function draw(now) {
        const t = (now / 1000) * speed, w = c.clientWidth, h = c.clientHeight, base = h * .62;
        ctx.clearRect(0, 0, w, h);

        /* 3本の波曲線（layer0=主線=最上 / layer1,2=残響=下にずれる）を点列で求める */
        const curve = [];
        for (let layer = 0; layer <= 2; layer++) {
          const pts = [];
          for (let x = 0; x <= w; x += 4) {
            const y = base + layer * 16
                    + lineY(x + layer * 120, t * (1 - layer * .22), w) * (1 - layer * .3);
            pts.push([x, y]);
          }
          curve.push(pts);
        }

        /* 主線(curve[0])を境界に、上下それぞれを縦グラデで塗る。
           top/bot が null の辺は画面端（0 / h）。stops は [位置0..1, 色] の配列。 */
        function band(top, bot, stops) {
          ctx.beginPath();
          if (top) { ctx.moveTo(top[0][0], top[0][1]); for (const p of top) ctx.lineTo(p[0], p[1]); }
          else { ctx.moveTo(0, 0); ctx.lineTo(w, 0); }
          if (bot) { for (let i = bot.length - 1; i >= 0; i--) ctx.lineTo(bot[i][0], bot[i][1]); }
          else { ctx.lineTo(w, h); ctx.lineTo(0, h); }
          ctx.closePath();
          const g = ctx.createLinearGradient(0, 0, 0, h);
          for (const s of stops) g.addColorStop(s[0], s[1]);
          ctx.fillStyle = g;
          ctx.fill();
        }
        /* 水平線（＝主線）の高さを主線の平均yから求め、波のうねりに追従させる。
           時刻の色（不透明）で空と海を塗る。水平線が最も明るく、上下へ向かうほど深く沈む。 */
        let my = 0; for (const p of curve[0]) my += p[1]; my /= curve[0].length;
        const hy = Math.max(.06, Math.min(.94, my / h));      /* 水平線の高さ 0..1 */
        const tod = dayColors(now);
        band(null,     curve[0], [[0, tod.sky], [Math.min(.99, hy), tod.hz]]);  /* 空：上→水平線 */
        band(curve[0], null,     [[Math.max(.01, hy), tod.hz], [1, tod.sea]]);  /* 海：水平線→下 */

        /* 琥珀の波線（境界線）を上に重ねる：手前の主線＋奥の残響 */
        for (let layer = 2; layer >= 0; layer--) {
          ctx.beginPath();
          const pts = curve[layer];
          ctx.moveTo(pts[0][0], pts[0][1]); for (let i = 1; i < pts.length - 1; i++) { const mx = (pts[i][0] + pts[i + 1][0]) / 2, my = (pts[i][1] + pts[i + 1][1]) / 2; ctx.quadraticCurveTo(pts[i][0], pts[i][1], mx, my); } { const e = pts[pts.length - 1]; ctx.lineTo(e[0], e[1]); }
          ctx.strokeStyle = layer
            ? `rgba(217,142,50,${.34 - layer * .08})`
            : 'rgba(217,142,50,.85)';
          ctx.lineWidth = layer ? 1 : 1.6;
          ctx.stroke();
        }
        if (running) rafId = requestAnimationFrame(draw);
      }

      if (this.staticMode) { draw(0); return; }   // reduce / 非対応：動かさず一枚だけ描く

      /* 画面外では止める（営みは続くが、無駄な火は焚かない） */
      const io = new IntersectionObserver((en) => {
        if (en[0].isIntersecting && !running) {
          running = true;
          rafId = requestAnimationFrame(draw);
        } else if (!en[0].isIntersecting) {
          running = false;
          cancelAnimationFrame(rafId);
        }
      });
      io.observe(c);
      this.onDispose(() => { running = false; cancelAnimationFrame(rafId); io.disconnect(); });
    }

    /* ---------- ジャーニー：一本の線の変形 ---------- */
    initJourney(journey) {
      const lineEl = journey.querySelector('[data-bj-line]');
      const echoEl = journey.querySelector('[data-bj-echo]');
      const cup = journey.querySelector('[data-bj-cup]');
      var _BJ_CUP_D = "M196 118 C198 135 241 148 300 147 C360 147 405 134 404 118 C400 102 355 89 297 90 C239 90 196 102 196 118 C199 169 207 222 218 274 C223 298 228 318 237 327 C251 342 328 345 350 330 C362 320 373 291 383 249 C423 226 455 199 444 170 C437 153 418 146 397 161 C400 145 402 130 404 118 C404 124 404 131 403 137 C425 127 447 135 460 155 C477 182 465 211 443 232 C421 254 396 268 375 282 C378 271 381 260 383 249 C390 222 396 187 397 161 C400 150 402 143 403 137";
      var _BJ_CUP_E = "M196 121 C198 138 241 151 300 150 C360 150 405 137 404 121 C400 105 355 92 297 93 C239 93 196 105 196 121 C199 172 207 225 218 277 C223 301 228 321 237 330 C251 345 328 348 350 333 C362 323 373 294 383 252 C423 229 455 202 444 173 C437 156 418 149 397 164 C400 148 402 133 404 121 C404 127 404 134 403 140 C425 130 447 138 460 158 C477 185 465 214 443 235 C421 257 396 271 375 285 C378 274 381 263 383 252 C390 225 396 190 397 164 C400 153 402 146 403 140";
      var _BJ_LOGO = 'M 134.084 46.030 C 125.996 48.178, 99.938 61.262, 88.599 68.871 C 84.292 71.760, 75.977 79.531, 66.482 89.537 L 51.267 105.573 49.895 114.537 C 46.246 138.381, 50.708 178.632, 57.856 186.357 C 65.361 194.466, 100.957 214.963, 122.500 223.581 C 129.100 226.221, 135.573 229.195, 136.885 230.191 C 139.769 232.379, 144.742 232.543, 146.974 230.524 C 147.871 229.712, 155.819 225.740, 164.636 221.697 C 187.218 211.343, 194.329 206.868, 213.500 190.941 C 228.910 178.139, 227.608 180.382, 228.277 165.492 C 228.598 158.346, 229.595 147.775, 230.491 142 C 232.874 126.650, 230.422 104.207, 225.829 99.318 C 224.130 97.509, 223.898 96.702, 224.813 95.787 C 226.720 93.880, 226.259 92.574, 221.536 86.491 C 211.639 73.747, 176.369 51.314, 159.406 46.976 C 147.673 43.975, 142.548 43.784, 134.084 46.030 M 137.992 55.533 C 130.910 57.218, 119.870 62.339, 104.085 71.263 C 92.611 77.749, 90.370 79.520, 78.697 91.324 C 62.182 108.026, 61.358 109.168, 59.526 117.875 C 56.868 130.512, 58.021 154.833, 62.031 170.725 C 63.708 177.370, 64.611 179.088, 68.246 182.549 C 76.114 190.039, 100.209 203.974, 119.189 212.011 C 125.960 214.879, 133.857 218.272, 136.737 219.551 L 141.975 221.878 157.896 214.578 C 166.653 210.563, 177.032 205.425, 180.962 203.159 C 188.985 198.533, 203.787 187.582, 211.835 180.316 L 217.169 175.500 218.557 163 C 222.248 129.750, 222.445 126.305, 221.159 117.413 C 220.476 112.691, 219.212 106.748, 218.349 104.206 C 217.190 100.794, 217.060 99.133, 217.852 97.865 C 220.343 93.877, 193.271 73.466, 168.441 60.609 C 158.732 55.583, 146.415 53.529, 137.992 55.533 M 112.791 79.551 C 108.582 81.835, 103.992 89.272, 103.327 94.884 C 102.596 101.063, 105.658 108.084, 112.539 116 C 123.777 128.930, 129 140.561, 129 152.657 C 129 160.059, 127.425 168, 125.957 168 C 125.558 168, 125.231 165.802, 125.231 163.115 C 125.231 147.642, 112.986 135, 97.999 135 C 90.181 135, 83.191 141.553, 83.048 149.016 C 82.920 155.741, 89.870 162.273, 97.155 162.273 C 104.168 162.273, 109.495 155.711, 105.950 151.440 C 104.178 149.305, 101 149.621, 101 151.933 C 101 155.751, 93.999 155.736, 91.954 151.913 C 90.525 149.243, 92.689 144.916, 96.324 143.173 C 98.852 141.962, 99.659 142.088, 104.475 144.444 C 118.203 151.160, 121.684 163.827, 112.543 173.801 L 109.207 177.442 98.577 177.835 C 89.474 178.172, 87.805 178.496, 86.951 180.091 C 86.185 181.522, 86.427 182.427, 87.995 183.995 C 89.915 185.915, 90.848 186, 103.768 185.438 C 111.321 185.109, 134.305 184.709, 154.844 184.548 C 191.751 184.259, 192.214 184.230, 194.344 182.067 L 196.500 179.878 194.004 177.829 C 191.744 175.975, 190.678 175.845, 182.754 176.465 C 177.939 176.842, 174 176.814, 174 176.404 C 174 175.993, 174.617 174.947, 175.371 174.079 C 178.655 170.297, 181.280 160.598, 181.244 152.379 C 181.207 143.744, 180.225 140.726, 176.870 138.930 C 173.306 137.023, 172.594 139.111, 172.496 151.756 C 172.422 161.447, 172.040 164.287, 170.312 168 C 167.154 174.789, 164.611 177, 159.961 177 C 156.030 177, 155.162 176.275, 156.909 174.450 C 160.881 170.300, 163.027 146.956, 161.068 129.211 C 159.082 111.233, 161.226 104.975, 170.769 100.885 C 181.952 96.091, 196.101 101.164, 199.949 111.347 C 202.389 117.806, 197.210 125.801, 189.205 127.935 C 185.250 128.989, 178.962 128.043, 176.536 126.030 C 174.564 124.393, 173.666 119.492, 174.912 117.164 C 176.330 114.514, 181.616 112.724, 184.099 114.053 C 186.304 115.233, 186.471 116.129, 184.800 117.800 C 183.931 118.669, 183.902 119.302, 184.694 120.094 C 187.866 123.266, 192.867 117.411, 191.055 112.645 C 188.026 104.678, 173.711 104.712, 168.453 112.699 C 167.284 114.473, 166.589 117.403, 166.554 120.692 C 166.506 125.286, 166.895 126.293, 169.924 129.420 C 174.813 134.467, 181.108 136.348, 188.902 135.093 C 196.731 133.832, 202.132 130.783, 205.413 125.772 C 208.924 120.409, 209.603 115.328, 207.609 109.346 C 202.440 93.842, 182.522 86.870, 165.924 94.753 C 161.243 96.976, 158.621 98.975, 156.827 101.688 C 151.990 109.004, 151.620 111.333, 152.863 126.606 C 154.396 145.427, 154.263 157.202, 152.432 165 C 151.592 168.575, 150.650 172.738, 150.339 174.250 C 149.856 176.597, 149.277 177, 146.387 177 C 142.930 177, 142.203 176.197, 144.019 174.381 C 144.579 173.821, 145.928 170.249, 147.016 166.443 C 150.899 152.866, 149.508 135.229, 143.038 116 C 139.120 104.354, 138.660 101.562, 139.993 97.520 C 141.778 92.113, 145.885 89.749, 152.781 90.160 C 157.938 90.467, 158.500 90.304, 158.500 88.509 C 158.500 86.191, 153.002 83.122, 148.718 83.048 C 145.432 82.991, 139.364 85.374, 136.645 87.788 C 133.927 90.202, 131.048 96.890, 131.022 100.852 C 131.010 102.696, 132.328 108.169, 133.951 113.013 C 139.632 129.970, 141.396 138.763, 141.281 149.546 C 141.180 159.038, 139.112 169.806, 136.844 172.645 C 136.341 173.274, 136.058 174.399, 136.215 175.145 C 136.389 175.973, 135.430 176.623, 133.750 176.816 C 132.238 176.990, 131 176.932, 131 176.687 C 131 176.442, 132.205 173.609, 133.679 170.391 C 138.721 159.376, 137.630 141.891, 131.097 129.035 C 129.450 125.793, 125.107 119.171, 121.448 114.320 C 113.956 104.389, 112.228 101.418, 111.910 97.926 C 111.360 91.887, 112.081 89.763, 116 85.883 C 120.354 81.572, 120.596 80.996, 118.800 79.200 C 117.267 77.667, 116.139 77.733, 112.791 79.551 M 84.834 122.128 C 79.370 123.665, 72.348 131.088, 70.513 137.267 C 68.631 143.607, 68.618 145.842, 70.442 150.207 C 72.165 154.330, 74.447 156.426, 76.398 155.677 C 77.493 155.257, 77.700 153.784, 77.294 149.304 C 76.298 138.327, 83.341 129, 92.626 129 C 95.673 129, 99.373 130.345, 106.450 134.024 C 111.764 136.788, 116.520 138.797, 117.018 138.489 C 118.432 137.615, 116.191 132.771, 113.187 130.205 C 108.875 126.523, 98.360 121.935, 93.232 121.499 C 90.629 121.277, 86.850 121.560, 84.834 122.128 M 123.315 188.151 C 116.063 188.509, 109.651 189.098, 109.065 189.460 C 107.487 190.435, 107.774 194.568, 109.520 196.017 C 110.722 197.014, 112.871 197.041, 119.770 196.143 C 124.572 195.519, 137.729 195.030, 149.010 195.057 C 171.785 195.111, 177.259 194.198, 177.820 190.249 C 178.004 188.954, 177.579 187.918, 176.820 187.807 C 173.497 187.320, 135.141 187.567, 123.315 188.151 M 120.058 200.430 C 119.385 201.240, 119.127 202.826, 119.485 203.953 C 120.567 207.363, 123.383 208.215, 129.705 207.047 C 132.819 206.471, 138.674 206, 142.716 206 C 151.302 206, 157.334 203.976, 156.814 201.269 C 156.507 199.670, 154.788 199.474, 138.878 199.228 C 124.354 199.003, 121.069 199.212, 120.058 200.430';
      var _BJ_CSS = ".bj-cupmark{opacity:1!important;visibility:visible!important}.bj-cup-draw{fill:none;stroke:var(--fg)!important;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}.bj-cup-draw.bj-cup-echo{stroke:var(--fg)!important;stroke-opacity:.3;stroke-width:1}.bj-cupmark .bj-logo-stamp{transform-box:fill-box;transform-origin:center;opacity:0}.bj-cupmark .bj-logo-stamp path{fill:var(--fg)!important;fill-rule:evenodd!important;stroke:none!important}.bj-step:last-child{align-items:flex-end;padding-bottom:23vh}@media(max-width:700px){.bj-step:last-child{padding-bottom:8vh}}";
      if (cup) {
        try {
          while (cup.firstChild) cup.removeChild(cup.firstChild);
          var _bjWrap = '<svg xmlns="http://www.w3.org/2000/svg"><g transform="translate(-10 178) scale(0.7)"><path class="bj-cup-draw bj-cup-echo" d="' + _BJ_CUP_E + '"/><path class="bj-cup-draw" d="' + _BJ_CUP_D + '"/><g transform="translate(263.1 178.6) scale(0.26)"><g class="bj-logo-stamp"><path fill-rule="evenodd" d="' + _BJ_LOGO + '"/></g></g></g></svg>';
          var _bjDoc = new DOMParser().parseFromString(_bjWrap, 'image/svg+xml');
          cup.appendChild(document.importNode(_bjDoc.documentElement.firstChild, true));
          var _bjDraws = cup.querySelectorAll('.bj-cup-draw');
          for (var _k = 0; _k < _bjDraws.length; _k++) { var _L = _bjDraws[_k].getTotalLength(); _bjDraws[_k].style.strokeDasharray = _L; _bjDraws[_k].style.strokeDashoffset = _L; }
          cup._bjDraws = _bjDraws; cup._bjLogo = cup.querySelector('.bj-logo-stamp');
        } catch (e) {}
        if (!document.getElementById('bj-stamp-style')) {
          var _bjSt = document.createElement('style'); _bjSt.id = 'bj-stamp-style'; _bjSt.textContent = _BJ_CSS; document.head.appendChild(_bjSt);
        }
      }
      if (!lineEl || !echoEl) return;

      /* シーン色は schema（テーマエディタ）から data-scenes JSON で受け取る */
      let scenes;
      try { scenes = JSON.parse(this.root.dataset.scenes || '[]'); } catch (e) { scenes = []; }
      const FALLBACK = [
        { bg: '#143A2E', fg: '#EAF0E8', accent: '#C8772B' },
        { bg: '#0E3B45', fg: '#E6EFEE', accent: '#D98E32' },
        { bg: '#3A2317', fg: '#F2ECDF', accent: '#D98E32' },
        { bg: '#E8D9C2', fg: '#3A2317', accent: '#B5612A' },
      ];
      while (scenes.length < 4) scenes.push(FALLBACK[scenes.length]);

      /* ---- 現在地ドット：4章のどこにいるかを左端で可視化 ---- */
      let dotEls = [];
      function setActiveDot(idx) {
        if (!dotEls.length) return;
        const n = Math.max(0, Math.min(dotEls.length - 1, Math.round(idx)));
        for (let k = 0; k < dotEls.length; k++) {
          const on = k === n;
          dotEls[k].classList.toggle('is-active', on);
          if (on) dotEls[k].setAttribute('aria-current', 'true');
          else dotEls[k].removeAttribute('aria-current');
        }
      }
      (function ensureDotStyle() {
        if (document.getElementById('bj-dots-style')) return;
        const st = document.createElement('style');
        st.id = 'bj-dots-style';
        st.textContent = ".bj-dots{position:fixed;left:clamp(10px,2vw,24px);top:50%;transform:translateY(-50%);z-index:6;display:flex;flex-direction:column;align-items:center;gap:7px;color:var(--fg);opacity:0;visibility:hidden;transition:opacity .4s ease,visibility .4s ease;pointer-events:none}\n.bj-dots.is-in{opacity:1;visibility:visible;pointer-events:auto}\n.bj-dot{position:relative;width:16px;height:16px;margin:0;padding:0;border:0;background:none;cursor:pointer;display:grid;place-items:center;color:inherit;-webkit-tap-highlight-color:transparent}\n.bj-dot>span{display:block;width:5px;height:5px;border-radius:50%;border:1px solid currentColor;background:transparent;opacity:.3;transition:transform .55s var(--bj-ease,cubic-bezier(.4,0,.1,1)),opacity .55s ease,background-color .55s ease,border-color .55s ease}\n.bj-dot:hover>span{opacity:.6}\n.bj-dot:focus-visible{outline:2px solid var(--accent);outline-offset:3px;border-radius:50%}\n.bj-dot.is-active>span{opacity:.95;background:var(--accent);border-color:var(--accent);transform:scale(1.4)}\n@media (max-width:680px){.bj-dots{left:3px;gap:5px}.bj-dot{width:14px;height:14px}.bj-dot>span{width:4px;height:4px}}\n@media (prefers-reduced-motion:reduce){.bj-dot>span{transition:none}}";
        document.head.appendChild(st);
      })();

      function setScene(i) {
        const s = scenes[Math.max(0, Math.min(3, i))];
        journey.style.setProperty('--bg', s.bg);
        journey.style.setProperty('--fg', s.fg);
        journey.style.setProperty('--accent', s.accent);
        if (cup) cup.classList.toggle('show', i === 3);
        setActiveDot(i);
      }
      setScene(0);

      const speed = this.speed;
      /* 点列を Catmull-Rom → 三次ベジェに変換して滑らかな曲線として描く。
         直線セグメントの折れ（特に湯気）が消え、全シーンが滑らかになる。 */
      function toSmoothPath(pts) {
        let d = 'M' + pts[0][0].toFixed(1) + ' ' + pts[0][1].toFixed(1);
        for (let k = 0; k < pts.length - 1; k++) {
          const p0 = pts[k - 1] || pts[k];
          const p1 = pts[k];
          const p2 = pts[k + 1];
          const p3 = pts[k + 2] || p2;
          const c1x = p1[0] + (p2[0] - p0[0]) / 6;
          const c1y = p1[1] + (p2[1] - p0[1]) / 6;
          const c2x = p2[0] - (p3[0] - p1[0]) / 6;
          const c2y = p2[1] - (p3[1] - p1[1]) / 6;
          d += 'C' + c1x.toFixed(1) + ' ' + c1y.toFixed(1) + ' '
                   + c2x.toFixed(1) + ' ' + c2y.toFixed(1) + ' '
                   + p2[0].toFixed(1) + ' ' + p2[1].toFixed(1);
        }
        return d;
      }
      function dFor(progress, t) {
        const i = Math.min(2, Math.floor(progress));
        const f = ss(Math.min(1, progress - i));
        /* 湯気（シーン3）は線がほぼ縦で、y方向の呼吸ゆらぎが「折れ」に見える。
           シーン3へ近づくほど呼吸を弱め、滑らかなS字を保つ。 */
        const bScale = 1 - 0.82 * Math.max(0, Math.min(1, progress - 2));
        const pts = [];
        for (let k = 0; k < N; k++) {
          const a = SHAPES[i][k], b = SHAPES[i + 1][k];
          const x = a[0] + (b[0] - a[0]) * f;
          const y = a[1] + (b[1] - a[1]) * f
                  + bScale * (3.5 * Math.sin(t * .45 + k * .35)        /* 呼吸（準周期） */
                            + 1.5 * Math.sin(t * .31 * PHI + k * .13));
          pts.push([x, y]);
        }
        return toSmoothPath(pts);
      }

      if (this.staticMode || !window.gsap || !window.ScrollTrigger) {
        lineEl.setAttribute('d', dFor(1, 0));   /* 静的：うねりの状態で固定 */
        echoEl.setAttribute('d', dFor(1, 2));
        return;
      }

      const gsap = window.gsap;
      gsap.registerPlugin(window.ScrollTrigger);

      acquireLenis();
      this.usesLenis = true;

      /* ヒーロー導入アニメーションは LCP 保護のため CSS keyframes に移植
         （brand-journey.css の bj-rise を参照）。JS では行わない。 */

      const triggers = [];
      const stepEls = Array.from(journey.querySelectorAll('.bj-step'));
      /* 形状モーフの進行を「各章の中心」に厳密同期させる。
         章 i が画面中央に来たとき shape=i、章 i→i+1 の間だけで線形にモーフ。
         以前はジャーニー全体スクロールを一律に 0→3 へ写していたため、
         章の中心に対して形が先行していた（モーフが早すぎる）。これを是正。
         scrub も廃し、各フレームで実際の章中心から算出するので位置ズレが出ない。 */
      const computeProgress = () => {
        if (stepEls.length < 2) return 0;
        const cy = innerHeight / 2;
        const centers = stepEls.map((s) => {
          const r = s.getBoundingClientRect();
          return r.top + r.height / 2;          // 各章の中心（ビューポート座標・上ほど小さい）
        });
        const last = centers.length - 1;
        if (cy <= centers[0]) return 0;          // 先頭章の中心に達する前
        if (cy >= centers[last]) return last;    // 末尾章の中心を過ぎた後
        for (let i = 0; i < last; i++) {
          if (cy >= centers[i] && cy <= centers[i + 1]) {
            return i + (cy - centers[i]) / (centers[i + 1] - centers[i]);
          }
        }
        return last;
      };

      const stepTweens = [];
      stepEls.forEach((step, i) => {
        triggers.push(window.ScrollTrigger.create({
          trigger: step, start: 'top center', end: 'bottom center',
          onToggle: (self) => { if (self.isActive) setScene(i); },
        }));
        const card = step.querySelector('.bj-step__card');
        if (card) {
          stepTweens.push(gsap.from(card, {
            y: 40, opacity: 0, duration: 1, ease: 'power3.out',
            scrollTrigger: { trigger: step, start: 'top 75%' },
          }));
        }
      });
      this.onDispose(() => {
        stepTweens.forEach((tw) => { if (tw.scrollTrigger) tw.scrollTrigger.kill(); tw.kill(); });
        triggers.forEach((tr) => tr.kill());
      });

      /* 線の描画ティッカー。画面外では更新を止める
         （§4.5 rAF の画面外停止をジャーニー側にも適用。見た目への影響なし） */
      /* 現在地ドットを生成（章ぶん）。色は journey の --accent/--fg を継承 */
      const dotsNav = document.createElement('nav');
      dotsNav.className = 'bj-dots';
      dotsNav.setAttribute('aria-label', 'ジャーニーの現在地');
      const reduce = this.reduce;
      stepEls.forEach((step, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bj-dot';
        const numEl = step.querySelector('.bj-num');
        const label = (numEl && numEl.textContent.trim()) || ('0' + (i + 1));
        btn.setAttribute('aria-label', label);
        btn.innerHTML = '<span></span>';
        btn.addEventListener('click', () => {
          const r = step.getBoundingClientRect();
          const y = Math.max(0, r.top + window.scrollY - (innerHeight - step.offsetHeight) / 2);
          if (lenis && !reduce) lenis.scrollTo(y, { duration: 1.0 });
          else window.scrollTo({ top: y, behavior: reduce ? 'auto' : 'smooth' });
        });
        dotsNav.appendChild(btn);
        dotEls.push(btn);
      });
      journey.appendChild(dotsNav);
      setActiveDot(Math.round(computeProgress()));
      /* 尾根（Earth）のラインが現れた時点で表示。.bj-stage（ライン本体の器）を監視 */
      const stageWrap = journey.querySelector('.bj-stage') || journey;
      const dotsIO = new IntersectionObserver((en) => {
        dotsNav.classList.toggle('is-in', en[0].isIntersecting);
      }, { rootMargin: '-45% 0px -30% 0px' });
      dotsIO.observe(stageWrap);
      this.onDispose(() => { dotsNav.remove(); dotsIO.disconnect(); });

      let stageVisible = true;
      const stageIO = new IntersectionObserver((en) => { stageVisible = en[0].isIntersecting; });
      stageIO.observe(journey);

      const tick = () => {
        if (!stageVisible) return;
        const t = (performance.now() / 1000) * speed;
        const progress = computeProgress();
        lineEl.setAttribute('d', dFor(progress, t));
        echoEl.setAttribute('d', dFor(Math.max(0, progress - .12), t * .8));
        if (cup && cup._bjDraws) {
          var _rev = Math.max(0, Math.min(1, (progress - 2.55) / 0.45));
          for (var _di = 0; _di < cup._bjDraws.length; _di++) { var _el = cup._bjDraws[_di]; var _ln = parseFloat(_el.style.strokeDasharray) || 0; _el.style.strokeDashoffset = (_ln * (1 - _rev)).toFixed(1); }
          if (cup._bjLogo) { var _lr = Math.max(0, Math.min(1, (_rev - 0.6) / 0.4)); cup._bjLogo.style.opacity = _lr; cup._bjLogo.style.transform = 'translateY(' + ((1 - _lr) * 24).toFixed(1) + 'px)'; }
          echoEl.style.opacity = (1 - _rev).toFixed(2);
        }
      };
      gsap.ticker.add(tick);
      this.onDispose(() => { gsap.ticker.remove(tick); stageIO.disconnect(); });
    }

    /* ---------- スキップ：イントロを飛ばして直下の商品セクションへ ---------- */
    initSkip() {
      const btn = this.root.querySelector('[data-bj-skip]');
      if (!btn) return;
      const hero = this.root.querySelector('.bj-hero');
      const cue = this.root.querySelector('.bj-scrollcue');

      /* SKIP（右）とスクロールキュー Scroll（中央）の bottom を常に一致させる。
         以前はキューが bottom:30px 固定でモバイルだと見切れ、SKIP だけ見えて
         先に押されてしまう事故があった。両者を同じ高さに揃えて回避する。
         高さは、サイト共通の右下チャットウィジェット（Shopify Inbox 等・最前面固定）が
         あればその上、無ければ既定値。チャットは遅延ロードのため数回リトライ＋リサイズで追従。 */
      const DEFAULT_BOTTOM = function(){ return innerWidth <= 680 ? 96 : 104; };
      const reposition = () => {
        if (!hero) return;
        let bv = DEFAULT_BOTTOM();
        const chat = document.querySelector('#ShopifyChat, inbox-online-store-chat');
        const cr = chat && chat.getBoundingClientRect();
        if (cr && cr.height && cr.top <= innerHeight) {
          const gap = 18;
          /* スクロール非依存で算出: ヒーロー下端のドキュメント座標から、
             チャット上端（ビューポート固定）の gap 上に bottom 端を合わせる。
             ヒーローがヘッダー分だけ下にずれて始まる点も自動で吸収する。 */
          const heroRect = hero.getBoundingClientRect();
          const heroDocBottom = heroRect.top + window.scrollY + hero.offsetHeight;
          bv = heroDocBottom - (cr.top - gap);
          bv = Math.min(Math.max(bv, 30), hero.offsetHeight * 0.5);
        }
        // SCROLL/SKIP を波ラインより下へ（ウィンドウ高に追従）。cue は hero 基準の absolute。
        // 波の基準線は canvas の 62%。hero 下端基準で「文字がライン下に来る上限」(_cap)で bv を抑え、
        // 低いウィンドウでは文字を画面内に収める床(_floor)を優先する。
        var _cnv = document.querySelector('[data-bj-canvas]');
        if (_cnv && hero) {
          var _cvr = _cnv.getBoundingClientRect();
          var _hr2 = hero.getBoundingClientRect();
          var _lineLowest = _cvr.top + _cnv.clientHeight * 0.62 + 45;  // 基準線＋振幅余裕
          var _cap = _hr2.bottom - _lineLowest - 16 - 24;              // 間隔16＋文字24 ぶん下げる
          var _floor = (_hr2.bottom - innerHeight) + 30;               // 画面内に収める床
          if (_cap < bv) bv = _cap;
          if (bv < _floor) bv = _floor;
        }
        if (bv < 20) bv = 20;
        bv = Math.round(bv);
        btn.style.bottom = bv + 'px';
        if (cue) { cue.style.bottom = bv + 'px'; }   // Scroll を SKIP と同じ高さに
      };
      reposition();
      const onResize = () => reposition();
      addEventListener('resize', onResize);
      const timers = [setTimeout(reposition, 800), setTimeout(reposition, 2200), setTimeout(reposition, 4500)];
      this.onDispose(() => { removeEventListener('resize', onResize); timers.forEach(clearTimeout); });

      const onClick = (e) => {
        e.preventDefault();
        const wrapper = this.root.closest('.shopify-section') || this.root;
        const next = wrapper.nextElementSibling;
        /* スティッキーヘッダー分を差し引いて、商品セクションの先頭が隠れないように */
        const headerGroup = document.querySelector('.shopify-section-group-header-group');
        const headerH = headerGroup ? headerGroup.offsetHeight : 0;
        const rectTop = next
          ? next.getBoundingClientRect().top
          : wrapper.getBoundingClientRect().bottom;
        const targetY = Math.max(0, rectTop + window.scrollY - headerH);
        if (lenis && !this.staticMode) {
          lenis.scrollTo(targetY, { duration: 1.1 });
        } else {
          window.scrollTo({ top: targetY, behavior: this.staticMode ? 'auto' : 'smooth' });
        }
      };
      btn.addEventListener('click', onClick);
      this.onDispose(() => btn.removeEventListener('click', onClick));

      /* SKIP をスクロールアニメーション（ジャーニー）の間も表示する。hero 側 SKIP は hero ごと流れて
         消えるため、ジャーニー側に画面固定の複製を置き、ヒーローを抜けたらクロスフェードで入れ替える。
         ジャーニー終端（ハンドオフ文章が入る時点）で消す。色は章の --fg に追従。 */
      const _journey = this.root.querySelector('[data-bj-journey]');
      if (_journey) {
        const _st = document.createElement('style');
        _st.textContent = ".bj-skip.bj-skip--float{position:fixed;z-index:6;color:var(--fg);opacity:0;visibility:hidden;transition:opacity .4s ease,visibility .4s ease,color 1.1s var(--bj-ease,ease)}\n" +
          ".bj-skip.bj-skip--float.is-in{opacity:.55;visibility:visible}\n" +
          ".bj-skip.bj-skip--float.is-in:hover,.bj-skip.bj-skip--float.is-in:focus-visible{opacity:1;color:var(--accent)}\n" +
          ".bj-skip.bj-skip--away{opacity:0;visibility:hidden;transition:opacity .4s ease,visibility .4s ease}";
        this.root.appendChild(_st);
        const _fl = btn.cloneNode(true);
        _fl.removeAttribute('style');
        _fl.classList.add('bj-skip--float');
        _journey.appendChild(_fl);
        _fl.addEventListener('click', onClick);
        /* 右下チャット（shopify-chat）は外枠が全画面で、ボタン本体は入れ子の shadow DOM 内。
           shadow を辿って右下に見えているボタン類の最上端を取り、その上に置く。 */
        const _chatTop = () => {
          let top = Infinity;
          const walk = (root, d) => {
            if (!root || d > 6) return;
            root.querySelectorAll('button, a, [role="button"]').forEach((e) => {
              const r = e.getBoundingClientRect();
              if (r.height > 0 && r.height < 120 && r.left > innerWidth * 0.5 && r.top > innerHeight * 0.5 && r.bottom <= innerHeight + 1) top = Math.min(top, r.top);
            });
            root.querySelectorAll('*').forEach((e) => { if (e.shadowRoot) walk(e.shadowRoot, d + 1); });
          };
          document.querySelectorAll('shopify-chat, #ShopifyChat, inbox-online-store-chat').forEach((c) => walk(c.shadowRoot || c, 0));
          return top;
        };
        const _place = () => {
          const t = _chatTop();
          _fl.style.bottom = (t < Infinity ? Math.round(innerHeight - t + 12) : 32) + 'px';
        };
        const _sync = () => {
          const hb = hero ? hero.getBoundingClientRect().bottom : 0;
          const jb = _journey.getBoundingClientRect().bottom;
          const on = hb < innerHeight * 0.6 && jb > innerHeight * 0.9;
          if (on && !_fl.classList.contains('is-in')) _place();   // チャットは遅延ロードのため出す直前に再計測
          _fl.classList.toggle('is-in', on);
          btn.classList.toggle('bj-skip--away', hb < innerHeight * 0.6);
        };
        const _onResize = () => { _place(); _sync(); };
        _place(); _sync();
        addEventListener('scroll', _sync, { passive: true });
        addEventListener('resize', _onResize);
        const _ft = [setTimeout(_place, 800), setTimeout(_place, 2200), setTimeout(_place, 4500)];
        this.onDispose(() => {
          removeEventListener('scroll', _sync); removeEventListener('resize', _onResize);
          _ft.forEach(clearTimeout); _fl.remove(); _st.remove();
          btn.classList.remove('bj-skip--away');
        });
      }
    }
  }

  /* ---------- ライフサイクル（テーマエディタ対応） ---------- */
  const instances = new Map();

  function init(root) {
    if (!root || instances.has(root)) return;
    try {
      instances.set(root, new BrandJourney(root));
    } catch (e) {
      /* 初期化に失敗してもHTMLは静的レイアウトのまま残る（固定スクロールで閲覧可能） */
      if (window.console && console.warn) console.warn('[brand-journey] アニメ初期化を見送り静的表示にフォールバック:', e);
    }
  }

  function destroyIn(container) {
    instances.forEach((inst, root) => {
      if (container === root || container.contains(root)) {
        inst.destroy();
        instances.delete(root);
      }
    });
  }

  function boot() {
    document.querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.matches && e.target.matches(SELECTOR)
      ? e.target
      : e.target.querySelector(SELECTOR);
    if (root) init(root);
  });
  document.addEventListener('shopify:section:unload', (e) => {
    destroyIn(e.target);
  });
})();
/* cache-bump 2026-06-27T07:42:32.840Z */
